import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Mediator } from '@spotahome/soyuz-mediator';
import { trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { Button, CHANNELS, FormInput } from '@spotahome/ui-library';
import DateRangeFilter from '@spotahome/ui-library/src/tenant/components/Filters/DateRangeFilter';
import { useMediaQuery } from '@spotahome/ui-library/src/tenant/components/utils/breakpoints/breakpoints';

import { formatDisplayDate } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';

import { usePricingContext } from '../../context/PricingContext';
import { BookNowPriceTable } from '../BookNowCard/BookNowPriceTable';

import './DateRangePicker.scss';

const noop = () => {};

const useCloseOnClickOutside = ({ initialShow, onOpen, onClose }) => {
  const [show, setShow] = useState(initialShow);
  const parentRef = useRef(null);

  const handleClose = e => {
    e.preventDefault();
    e.stopPropagation();
    const path = e.composedPath && e.composedPath();
    if (!path?.includes(parentRef.current)) {
      setShow(false);
      onClose();
    }
  };

  const handleOpen = e => {
    e?.preventDefault();
    e?.stopPropagation();
    setShow(true);
    onOpen();
  };

  const forceClose = () => {
    setShow(false);
    onClose();
  };

  useEffect(() => {
    if (show) {
      document.addEventListener('click', handleClose);
    }
    return () => {
      document.removeEventListener('click', handleClose);
    };
  }, [show]);

  return { show, parentRef, handleOpen, forceClose };
};

const DateRangePicker = ({
  moveIn,
  minStay,
  moveOut,
  onChange,
  minDate,
  disableDayFnMoveIn,
  disableDayFnMoveOut,
  cityId,
  onOpen = noop,
  onClose = noop
}) => {
  const {
    show: showPicker,
    parentRef,
    handleOpen,
    forceClose
  } = useCloseOnClickOutside({
    initialShow: false,
    onClose,
    onOpen
  });
  const { pricingDetails } = usePricingContext();
  const { isBiggerThanTablet } = useMediaQuery();

  const hasPricingDetails = useMemo(
    () => Object.keys(pricingDetails).length > 0,
    [pricingDetails]
  );

  const datesI18n = {
    dateInPlaceholder: trans('property_show.book_now_card.placeholder'),
    dateOutPlaceholder: trans('property_show.book_now_card.placeholder'),
    dateInLabel: trans('property_show.book_now_card.date_in'),
    dateOutLabel: trans('property_show.book_now_card.date_out')
  };

  const handleDateRangeChange = ({
    moveIn: newMoveIn,
    moveOut: newMoveOut
  }) => {
    const change = {};
    if (newMoveIn) {
      change.moveIn = newMoveIn;
    }

    if (newMoveOut) {
      change.moveOut = newMoveOut;
    }

    onChange(change);
  };

  const handleClearDates = e => {
    e.preventDefault();
    e.stopPropagation();

    SoyuzAnalytics.sendGA4Event('send', {
      context: 'book-now',
      section: 'date-picker',
      action: 'clear-dates'
    });

    Mediator.publish(CHANNELS.REFRESH_CALENDAR_DATEPICKER);
  };

  const handleApplyDatesClick = () => {
    SoyuzAnalytics.sendGA4Event('send', {
      context: 'book-now',
      section: 'date-picker',
      action: 'apply-dates'
    });
    forceClose();
  };

  return (
    <div ref={parentRef}>
      {!showPicker && (
        <div
          onClick={handleOpen}
          role="button"
          tabIndex={0}
          className="date-range-picker__input"
        >
          <FormInput
            dataTest="date-input-from"
            placeholder={datesI18n.dateInPlaceholder}
            title={datesI18n.dateInLabel}
            readOnly
            bold
            name="date-input-from"
            type="text"
            value={moveIn ? formatDisplayDate(moveIn) : ''}
          />
          <span className="date-range-picker__arrow" />
          <FormInput
            dataTest="date-input-to"
            placeholder={datesI18n.dateOutPlaceholder}
            title={datesI18n.dateOutLabel}
            readOnly
            bold
            name="date-input-to"
            type="text"
            value={moveOut ? formatDisplayDate(moveOut) : ''}
          />
        </div>
      )}

      {showPicker && (
        <div className="date-range-picker">
          <DateRangeFilter
            minDate={minDate}
            from={moveIn}
            minStay={minStay}
            onDatesChange={handleDateRangeChange}
            to={moveOut}
            initiallyOpen
            disableDayFnMoveIn={disableDayFnMoveIn}
            disableDayFnMoveOut={disableDayFnMoveOut}
            showFlexibleOptions={false}
            keepMoveOutCalendarOpen={isBiggerThanTablet()}
            onClickBack={forceClose}
            calendarClassnames="date-range-picker__calendar"
          />
          <div className="date-range-picker__footer">
            {hasPricingDetails && (
              <BookNowPriceTable
                priceClassName="date-range-picker__price"
                reservationPrice={pricingDetails.reservationPrice}
                bookingFee={pricingDetails.bookingFee}
                cityId={cityId}
              />
            )}
            <div className="date-range-picker__buttons">
              <Button
                onClick={handleClearDates}
                fullWidth
                color="link"
                data-test={`listing-dates-clear`}
              >
                {trans('search.exposedfilters.dates.clear')}
              </Button>
              <Button
                onClick={handleApplyDatesClick}
                fullWidth
                color="primary"
                data-test={`listing-dates-apply`}
                size="large"
                className="date-range-picker__apply-button"
              >
                {trans('property_show.book_now_card.apply_dates')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DateRangePicker.propTypes = {
  moveIn: PropTypes.string.isRequired,
  minStay: PropTypes.number.isRequired,
  moveOut: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.string.isRequired,
  disableDayFnMoveIn: PropTypes.func.isRequired,
  disableDayFnMoveOut: PropTypes.func.isRequired,
  cityId: PropTypes.string.isRequired,
  onOpen: PropTypes.func,
  onClose: PropTypes.func
};

export default DateRangePicker;
